/***************************************************************************************************
 * APPLICATION IMPORTS
 */

import "url-search-params-polyfill"; // npm install url-search-params-polyfill --save

if (typeof Array.prototype.at !== "function") {
  document.body.setAttribute("data-is-legacy", "true");
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/at
  Object.defineProperty(Array.prototype, "at", {
    value: function (index: number): unknown {
      // eslint-disable-next-line @stylistic/quotes
      if (this === null || this === undefined) throw new TypeError('"this" is null or not defined');
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const t: Array<unknown> = this;
      index = Math.trunc(index);
      if (index < 0) {
        return t[t.length + index];
      }
      return t[index];
    },
  });
}

if (typeof Array.prototype.flatMap !== "function") {
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/flatMap
  Object.defineProperty(Array.prototype, "flatMap", {
    value: function <T, U>(callbackfn: (value: T, index: number, array: T[]) => U | ReadonlyArray<U>, thisArg?: unknown): U[] {
      // eslint-disable-next-line @stylistic/quotes
      if (this === null || this === undefined) throw new TypeError('"this" is null or not defined');
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const t: Array<T> = this;
      const result: U[] = new Array(0);
      const l: number = t.length;
      const cFn: (value: T, index: number, array: T[]) => U | ReadonlyArray<U> =
        thisArg === undefined
          ? callbackfn
          : function (v, i, a): U | ReadonlyArray<U> {
              return callbackfn.call(thisArg, v, i, a);
            };
      for (let i = 0; i < l; ++i)
        if (i in t) {
          const value: U | ReadonlyArray<U> = cFn(t[i], i, t);
          if (value instanceof Array || Array.isArray(value)) {
            const vLength: number = value.length;
            if (vLength) {
              let rIndex: number = result.length;
              let skipped: number = 0;
              result.length += vLength;
              for (let vIndex = 0; vIndex < vLength; ++vIndex)
                if (vIndex in value) {
                  result[rIndex++] = value[vIndex];
                } else ++skipped;
              if (skipped) result.length -= skipped;
            }
          } else result[result.length] = value;
        }
      return result;
    },
  });
}

if (typeof Array.prototype.flat !== "function") {
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/flat
  Object.defineProperty(Array.prototype, "flat", {
    value: function <T>(depth?: number): unknown[] {
      // eslint-disable-next-line @stylistic/quotes
      if (this === null || this === undefined) throw new TypeError('"this" is null or not defined');
      function _flat(t: T[], _depth: number): unknown[] {
        let result: unknown[] = new Array(0);
        const l: number = t.length;
        for (let i = 0; i < l; ++i)
          if (i in t) {
            const value = t[i];
            if (_depth > 0 && (value instanceof Array || Array.isArray(value))) {
              result = result.concat(_flat(value, _depth - 1));
            } else result[result.length] = value;
          }
        return result;
      }
      return _flat(this, depth !== undefined ? depth : 1);
    },
  });
}

if (typeof Array.prototype.fill !== "function") {
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/fill
  Object.defineProperty(Array.prototype, "fill", {
    value: function <T>(value: T, start?: number, end?: number): T[] {
      // eslint-disable-next-line @stylistic/quotes
      if (this === null || this === undefined) throw new TypeError('"this" is null or not defined');
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const t: Array<T> = this;
      const len: number = t.length >>> 0;
      const relativeStart: number = (start || 0) >> 0;
      let k: number = relativeStart < 0 ? Math.max(len + relativeStart, 0) : Math.min(relativeStart, len);
      const relativeEnd: number = end === undefined ? len : end >> 0;
      const finalValue: number = relativeEnd < 0 ? Math.max(len + relativeEnd, 0) : Math.min(relativeEnd, len);
      while (k < finalValue) {
        t[k] = value;
        k++;
      }
      return t;
    },
  });
}

if (typeof String.prototype.endsWith !== "function") {
  Object.defineProperty(String.prototype, "endsWith", {
    value: function (searchString: string, position: number | undefined): boolean {
      // eslint-disable-next-line @stylistic/quotes
      if (this === null || this === undefined) throw new TypeError('"this" is null or not defined');
      const t = (<unknown>this)!.toString();
      if (typeof position !== "number" || !isFinite(position) || Math.floor(position) !== position || position > t.length) {
        position = t.length;
      }
      position -= searchString.length;
      const lastIndex = t.lastIndexOf(searchString, position);
      return lastIndex !== -1 && lastIndex === position;
    },
  });
}

if (typeof String.prototype.includes !== "function") {
  Object.defineProperty(String.prototype, "includes", {
    value: function (): boolean {
      "use strict";
      // eslint-disable-next-line prefer-rest-params
      return String.prototype.indexOf.apply(this, arguments as unknown as Parameters<string["indexOf"]>) !== -1;
    },
  });
}

if (typeof Array.prototype.includes !== "function") {
  Object.defineProperty(Array.prototype, "includes", {
    value: function (): boolean {
      "use strict";
      // eslint-disable-next-line prefer-rest-params
      return Array.prototype.indexOf.apply(this, arguments as unknown as Parameters<Array<unknown>["indexOf"]>) !== -1;
    },
  });
}

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/WeakRef
(function (global) {
  if (global && typeof global === "object" && typeof global["WeakRef"] === "undefined") {
    global.WeakRef = (function (weakMap) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      function WeakRef(this: object, target: object): void {
        weakMap.set(this, target);
      }
      WeakRef.prototype.deref = function (): object {
        return weakMap.get(this);
      };
      return WeakRef;
    })(new WeakMap());
  }
})(
  (function () {
    if (typeof globalThis === "object" && globalThis) return globalThis;
    if (typeof self === "object" && self) return self;
    if (typeof window === "object" && window) return window;
    // if (typeof global === "object" && global) return global;
    if (typeof Function === "function") return Function("return this")();
    return null;
  })()
);
